<template>
  <div
    v-if="isVisible && isDesktop"
    ref="root"
    class="asPayFailBanner asPayFailBanner--desktop"
    :class="{ 'asPayFailBanner--aboveConsent': isConsentVisible }"
    data-test="payment-failure-banner"
  >
    <div class="asPayFailBanner_row">
      <div class="asPayFailBanner_message">
        <div class="asPayFailBanner_titleRow">
          <span class="asPayFailBanner_title">{{ $t('paymentFailure.title') }}</span>
          <span class="asPayFailBanner_icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                fill="currentColor"
                d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8Zm0 3.4a.9.9 0 0 1 .9.95l-.25 4.2a.65.65 0 0 1-1.3 0l-.25-4.2A.9.9 0 0 1 8 3.4Zm0 7.1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
              />
            </svg>
          </span>
        </div>
        <p class="asPayFailBanner_subtitle">{{ $t('paymentFailure.subtitle') }}</p>
      </div>

      <div
        class="asPayFailBanner_actions"
        :class="{ 'asPayFailBanner_actions--bare': isActionsBare }"
      >
        <button
          v-if="canDecline"
          type="button"
          class="asPayFailBanner_decline"
          data-test="payment-failure-decline"
          @click="onDecline"
        >{{ $t('paymentFailure.declineBtn') }}</button>
        <button
          v-if="canResume"
          type="button"
          class="asPayFailBanner_resume"
          data-test="payment-failure-resume"
          @click="onResume"
        >{{ $t('superSubscription.status.resumeBtn') }}</button>
        <button
          type="button"
          class="asPayFailBanner_close"
          data-test="payment-failure-close"
          :aria-label="$t('close')"
          @click="onClose"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            <path d="M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  </div>

  <div
    v-else-if="isVisible"
    ref="root"
    class="asPayFailBanner asPayFailBanner--mobile"
    data-test="payment-failure-banner"
  >
    <button
      v-if="!isExpanded"
      type="button"
      class="asPayFailBanner_strip"
      data-test="payment-failure-strip"
      @click="onExpand"
    >
      <span class="asPayFailBanner_icon" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            fill="currentColor"
            d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8Zm0 3.4a.9.9 0 0 1 .9.95l-.25 4.2a.65.65 0 0 1-1.3 0l-.25-4.2A.9.9 0 0 1 8 3.4Zm0 7.1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
          />
        </svg>
      </span>
      <span class="asPayFailBanner_title">{{ $t('paymentFailure.title') }}</span>
      <span class="asPayFailBanner_details" data-test="payment-failure-details">
        {{ $t('paymentFailure.detailsLink') }}
      </span>
    </button>

    <div v-else class="asPayFailBanner_body">
      <div class="asPayFailBanner_header">
        <div class="asPayFailBanner_titleRow">
          <span class="asPayFailBanner_icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                fill="currentColor"
                d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8Zm0 3.4a.9.9 0 0 1 .9.95l-.25 4.2a.65.65 0 0 1-1.3 0l-.25-4.2A.9.9 0 0 1 8 3.4Zm0 7.1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
              />
            </svg>
          </span>
          <span class="asPayFailBanner_title">{{ $t('paymentFailure.title') }}</span>
          <button
            type="button"
            class="asPayFailBanner_close"
            data-test="payment-failure-close"
            :aria-label="$t('close')"
            @click="onClose"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <p class="asPayFailBanner_subtitle">{{ $t('paymentFailure.subtitle') }}</p>
      </div>

      <div class="asPayFailBanner_actions">
        <button
          v-if="canDecline"
          type="button"
          class="asPayFailBanner_decline"
          data-test="payment-failure-decline"
          @click="onDecline"
        >{{ $t('paymentFailure.declineBtn') }}</button>
        <button
          v-if="canResume"
          type="button"
          class="asPayFailBanner_resume"
          data-test="payment-failure-resume"
          @click="onResume"
        >{{ $t('superSubscription.status.resumeBtn') }}</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// onMounted / onBeforeUnmount / ref / nextTick понадобятся в задаче 6 —
// импортируем сразу, чтобы не переписывать шапку дважды.
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useMq } from 'vue3-mq'
import usePaymentFailureWarning from '@web/platform.v3/components/payment-failure/usePaymentFailureWarning'
import { useCookieConsent } from '@web/platform.v3/composables/useCookieConsent'

defineOptions({ name: 'PaymentFailureBanner' })

const mq = useMq()
const isDesktop = computed<boolean>(() => mq.current === 'desktop')

const {
  isVisible, isExpanded, canResume, canDecline,
  onResume, onDecline, onClose, onExpand, trackView
} = usePaymentFailureWarning()

// Десктопный блок действий резервирует фиксированную ширину под обе кнопки
// (см. стили). Если контракт не разрешил ни одну — резервировать нечего,
// иначе рядом с «×» остаётся ~480px пустоты.
const isActionsBare = computed<boolean>(() => !canResume.value && !canDecline.value)

// Событие показа: раньше оно уходило из onMounted, но плашка теперь монтируется
// в App.vue раньше, чем загружаются подписки, — на момент монтирования
// isVisible ещё ложен, и событие не ушло бы никогда.
//
// viewTracked — защита от повторной отправки: при immediate: true колбэк с уже
// истинным isVisible выполняется синхронно внутри самого вызова watch(), до
// того как переменной stopViewWatch успевает присвоиться возвращаемая функция
// остановки (без заглушки — ReferenceError, temporal dead zone). В этот момент
// stopViewWatch ещё указывает на плейсхолдер-no-op, поэтому реальный watcher
// остался бы висеть живым, и повторный переход isVisible false → true отправил
// бы trackView() второй раз. viewTracked гарантирует ровно один вызов
// независимо от того, наступил показ сразу при монтировании или позже; строка
// после watch() довызывает уже настоящий stopViewWatch, если колбэк успел
// сработать в immediate-фазе.
let viewTracked = false
let stopViewWatch: () => void = () => {}
stopViewWatch = watch(isVisible, visible => {
  if (!visible || viewTracked) return
  viewTracked = true
  trackView()
  stopViewWatch()
}, { immediate: true })
if (viewTracked) stopViewWatch()

// Cookie-consent тоже прижат к низу окна и лежит выше по слою: пока он висит,
// островок поднимаем над ним, иначе его не видно целиком и «×» недоступен.
const { isVisible: isConsentVisible } = useCookieConsent()

const OFFSET_VAR = '--as-payment-warning-offset'
const root = ref<HTMLElement | null>(null)
let observer: ResizeObserver | undefined

// Отступ нужен только мобильной полоске: она fixed сверху и без него легла бы
// поверх шапки. Десктопный островок висит внизу поверх контента — по ТЗ так и надо.
function writeOffset (): void {
  if (!isVisible.value || isDesktop.value || !root.value) {
    document.documentElement.style.removeProperty(OFFSET_VAR)
    return
  }
  document.documentElement.style.setProperty(OFFSET_VAR, `${root.value.offsetHeight}px`)
}

onMounted(() => {
  writeOffset()
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(writeOffset)
    if (root.value) observer.observe(root.value)
  }
})

watch([isVisible, isDesktop, isExpanded], async () => {
  await nextTick()
  writeOffset()
  // disconnect() перед observe(): при переключении раскладки через 1244px
  // DOM-узел за ref="root" заменяется, а без отсоединения старого узла
  // наблюдатель держит на него сильную ссылку до размонтирования компонента —
  // которое, раз он теперь один на всё приложение, попросту не наступает.
  if (observer && root.value) {
    observer.disconnect()
    observer.observe(root.value)
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
  document.documentElement.style.removeProperty(OFFSET_VAR)
})
</script>

<style lang="scss" scoped>
.asPayFailBanner {
  box-sizing: border-box;
  // Ниже тостов (9999), cookie-consent (99997) и модалок (99999),
  // выше липкой десктопной шапки (10) и мобильной (1).
  z-index: 9990;
  background: var(--ac-color-orange-substrate-deep);
  font-family: Inter, sans-serif;

  &--desktop {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    // Ширина верхнего меню (headerDesktop max-width 1217px) — по ТЗ островок
    // совпадает с ним по ширине. 32px — боковые поля на узком десктопе.
    width: min(1217px, 100% - 32px);
    padding: 16px 20px 20px;
    border-radius: 22px;
    box-shadow: 0 4px 10px 2px rgba(41, 84, 251, 0.1);

    .asPayFailBanner_actions {
      // Фиксированная ширина блока действий из выгрузки Figma (Actions Row:
      // flex: none; width: 498px). Без неё блок сжимается по контенту, и вся
      // свободная ширина островка уходит подзаголовку, а resume-кнопка выходит
      // заметно уже макета. Внутри resume остаётся flex: 1 1 auto и занимает
      // остаток строки за вычетом decline и close.
      flex: 0 0 498px;
      width: 498px;

      // Ни decline, ни resume недоступны контрактом (canResume: false у
      // примерно половины отменённых подписок) — резервировать 498px не подо
      // что, кроме «×»: рядом с ним остаётся пустота почти во всю ширину блока.
      &--bare {
        flex: none;
        width: auto;
      }
    }
  }

  // Высота баннера согласия плюс тот же отступ 24px.
  &--aboveConsent { bottom: 114px; }

  &--mobile {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;

    // В свёрнутом состоянии заголовок не должен переопределять десктопные
    // 18px/28px — переопределяем локально, а не в общем блоке &_title.
    .asPayFailBanner_title {
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      opacity: 0.9;
    }

    .asPayFailBanner_subtitle {
      // Текст выравнивается под заголовок, а не под иконку (16px иконки + 6px gap).
      padding-left: 22px;
      font-size: 12px;
      line-height: 18px;
      font-weight: 400;
      opacity: 0.5;
    }

    .asPayFailBanner_titleRow {
      .asPayFailBanner_title { flex: 1 1 auto; }
    }

    .asPayFailBanner_actions {
      gap: 12px;
    }

    .asPayFailBanner_decline,
    .asPayFailBanner_resume {
      min-height: 38px;
      font-size: 12px;
      line-height: 18px;
      font-weight: 500;
    }

    // Разница с десктопом намеренная: там подчёркнута и в цвете deep-navy-blue,
    // здесь — без подчёркивания и серая. Так в макете, сверено с обеими выгрузками.
    //
    // opacity: 0.6 из базового блока — десктопное значение (Label/Secondary
    // поверх deep-navy-blue). В мобильной выгрузке у этой кнопки сплошной
    // dark-gray без прозрачности, поэтому здесь она явно сброшена — не убирать.
    .asPayFailBanner_decline {
      color: var(--ac-color-dark-gray);
      text-decoration: none;
      opacity: 1;
    }
  }

  &_strip {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    // 48px итого: 14 сверху, 20 содержимое, 14 снизу. В макете отступы
    // несимметричны (8 сверху, 16 снизу, высота 44) — здесь сознательно
    // выровнены, чтобы текст стоял по центру полоски.
    padding: 14px 18px;
    text-align: left;

    // Заголовок растягивается и прижимает «Подробнее» к правому краю полоски —
    // без flex-grow заголовок занимает только ширину текста, и «Подробнее»
    // встаёт сразу за ним с дырой до правого поля.
    .asPayFailBanner_title {
      flex: 1 1 auto;
    }
  }

  &_details {
    flex: none;
    font-size: 12px;
    line-height: 18px;
    font-weight: 500;
    color: var(--ac-color-orange);
    text-decoration: underline;
  }

  &_body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px 18px;
  }

  // Заголовок и описание — одна группа, между ними 2px. Без этой обёртки они
  // становятся соседями в колонке _body и разъезжаются на её gap в 12px,
  // а 12px в макете стоят только между этой группой и кнопками.
  &_header {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &_row {
    display: flex;
    align-items: flex-start;
    gap: 22px;
  }

  &_message {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 2px;
  }

  &_titleRow {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &_title {
    font-size: 18px;
    line-height: 28px;
    font-weight: 500;
    color: var(--ac-color-deep-navy-blue);
  }

  &_subtitle {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: var(--ac-color-deep-navy-blue);
    opacity: 0.7;
  }

  &_icon {
    display: flex;
    flex: none;
    width: 16px;
    height: 16px;
    color: var(--ac-color-orange);
  }

  &_actions {
    display: flex;
    align-items: flex-start;
    gap: 14px;
  }

  // Кнопки — обычные <button>, сбрасываем нативные стили. &_strip тоже
  // <button> (вся свёрнутая полоска — зона тапа) — без сброса он получает
  // нативные фон/рамку браузера, которые перекрывают подложку orange-substrate-deep
  // и добавляют 4px к высоте полоски за счёт рамки 2px сверху и снизу,
  // а шрифт кнопки (Arial) не даёт потомкам унаследовать Inter.
  &_decline,
  &_resume,
  &_close,
  &_strip {
    box-sizing: border-box;
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
  }

  &_decline {
    padding: 10px 14px;
    min-height: 50px;
    font-size: 14px;
    line-height: 20px;
    // Совпадает с UA-дефолтом кнопки (400) уже сегодня — фиксируем явно,
    // чтобы будущий глобальный reset не поменял вес молча.
    font-weight: 400;
    color: var(--ac-color-deep-navy-blue);
    opacity: 0.6;
    text-decoration: underline;
  }

  &_resume {
    flex: 1 1 auto;
    padding: 10px 12px;
    min-height: 50px;
    border-radius: 28px;
    background: var(--ac-color-deep-liliac);
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: var(--ac-color-white);
  }

  &_close {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    color: var(--ac-color-dark-gray);
  }
}
</style>
